import React, { useState, useEffect } from 'react';
import {
  Flex,
  Typography,
  Button,
  Card,
  Table,
  Tag,
  Avatar,
  Modal,
  Form,
  Input,
  Select,
  Spin,
  Space,
  Row,
  Col,
  Popover,
  Divider,
  Checkbox
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  ReloadOutlined,
  PhoneOutlined,
  MailOutlined,
  BarChartOutlined,
  BankOutlined,
  TrophyOutlined,
  RiseOutlined
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../../i18n/TranslationContext';
import { useNotification } from '../../hooks/useNotification';
import DashboardService from '../../service';
import CreatorInfo from '../../components/CreatorInfo';

const ManagersPage = () => {
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const { lang } = useParams();
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const [form] = Form.useForm();
  const [managers, setManagers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingManager, setEditingManager] = useState(null);
  const [statsPopoverOpen, setStatsPopoverOpen] = useState(null);
  const [selectedManagerStats, setSelectedManagerStats] = useState(null);

  useEffect(() => {
    loadManagers();
    loadBranches();
  }, []);

  const loadManagers = async () => {
    try {
      setLoading(true);
      const data = await DashboardService.getManagers(1, 100, 'createdAt', 'desc');
      if (data.users) {
        setManagers(data.users);
      }
    } catch (error) {
      console.error('Failed to load managers:', error);
      showError('Failed to load managers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadBranches = async () => {
    try {
      const data = await DashboardService.getBranches();
      if (data.branches) {
        setBranches(data.branches);
      }
    } catch (error) {
      console.error('Failed to load branches:', error);
    }
  };

  const handleCreate = () => {
    setEditingManager(null);
    form.resetFields();
    form.setFieldsValue({
      firstName: '', lastName: '', username: '', password: '', confirmPassword: '',
      email: '', phone: '', branchId: undefined, language: 'en',
      description: '', note: '', log_out: false,
      copart_on: true, iaai_on: true, manheim_on: true, prebid: true, live_bid: true
    });
    setIsFormOpen(true);
  };

  const handleEdit = (manager) => {
    setEditingManager(manager);
    form.setFieldsValue({
      firstName: manager.firstName || manager.name?.split(' ')[0] || '',
      lastName: manager.lastName || manager.name?.split(' ').slice(1).join(' ') || '',
      username: manager.user_name || '',
      password: '',
      confirmPassword: '',
      email: manager.email || '',
      phone: manager.phone || '',
      branchId: manager.branchId || undefined,
      language: manager.language || 'en',
      description: manager.description || '',
      note: manager.note || '',
      log_out: manager.log_out || false,
      copart_on: manager.copart_on !== false,
      iaai_on: manager.iaai_on !== false,
      manheim_on: manager.manheim_on !== false,
      prebid: manager.prebid !== false,
      live_bid: manager.live_bid !== false
    });
    setIsFormOpen(true);
  };

  const handleDelete = async (managerId, managerName) => {
    if (!window.confirm(`Are you sure you want to delete manager "${managerName}"?`)) return;
    try {
      const result = await DashboardService.deleteUser(managerId);
      if (result.success) {
        showSuccess(result.message || 'Manager deleted successfully!');
        loadManagers();
      } else {
        showError(result.error || result.message || 'Failed to delete manager');
      }
    } catch (error) {
      console.error('Failed to delete manager:', error);
      showError(error.message || 'Failed to delete manager. Please try again.');
    }
  };

  const handleFormSubmit = async (values) => {
    const branch = branches.find(b => b.id === parseInt(values.branchId));
    if (editingManager) {
      const updateData = {
        name: `${values.firstName} ${values.lastName}`,
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        branchId: values.branchId ? parseInt(values.branchId) : editingManager.branchId,
        language: values.language,
        description: values.description,
        note: values.note,
        log_out: values.log_out,
        copart_on: values.copart_on,
        iaai_on: values.iaai_on,
        manheim_on: values.manheim_on,
        prebid: values.prebid,
        live_bid: values.live_bid
      };
      if (values.password?.trim()) updateData.password = values.password;
      if (branch) updateData.branchName = branch.name;

      const managerId = editingManager.user_id || editingManager.managerId || editingManager.autoIncrementId || editingManager.id;
      if (!managerId) {
        showError('Cannot update manager: ID not found');
        return;
      }

      const result = await DashboardService.updateUser(managerId, updateData);
      if (result.success) {
        showSuccess('Manager updated successfully!');
        loadManagers();
        setIsFormOpen(false);
        setEditingManager(null);
      } else {
        showError(result.error || 'Failed to update manager');
      }
    } else {
      const managerData = {
        user_id: `USER${Date.now()}`,
        first_uuid: `UUID1-${Date.now()}`,
        second_uuid: `UUID2-${Date.now()}`,
        user_name: values.username,
        password: values.password,
        email: values.email,
        role: 'manager',
        name: `${values.firstName} ${values.lastName}`,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        branchId: values.branchId ? parseInt(values.branchId) : null,
        branchName: branch?.name,
        language: values.language,
        description: values.description,
        note: values.note,
        log_out: values.log_out,
        copart_on: values.copart_on,
        iaai_on: values.iaai_on,
        manheim_on: values.manheim_on,
        prebid: values.prebid,
        live_bid: values.live_bid,
        permissions: ['manage_subdealers', 'view_branch_data', 'manage_accounts', 'create_subdealers']
      };

      const result = await DashboardService.createUser(managerData);
      if (result.success) {
        showSuccess('Manager created successfully!');
        loadManagers();
        setIsFormOpen(false);
      } else {
        showError(result.error || 'Failed to create manager');
      }
    }
  };

  const handleManagerClick = (manager) => {
    const managerId = manager.user_id || manager.managerId || manager.autoIncrementId || manager.id;
    if (managerId) navigate(`/${lang}/managers/${managerId}`);
  };

  const getBranchName = (branchId) => {
    const branch = branches.find(b => b.id === branchId || b.id === parseInt(branchId));
    return branch ? branch.name : 'Unknown';
  };

  const getBranchFlag = (branchId) => {
    const branch = branches.find(b => b.id === branchId || b.id === parseInt(branchId));
    return branch ? branch.flag : '🏢';
  };

  const stats = [
    { title: t('managers.totalManagers'), value: managers.length, icon: <UserOutlined />, color: '#1e3a5f' },
    ...branches.slice(0, 3).map((branch, index) => ({
      title: `${branch.name} Managers`,
      value: managers.filter(m => m.branchId === branch.id).length,
      icon: <UserOutlined />,
      color: ['#22c55e', '#f59e0b', '#8b5cf6'][index] || '#64748b'
    }))
  ];

  const statsPopoverContent = selectedManagerStats && (
    <div style={{ minWidth: 280, maxWidth: 360 }}>
      <Flex align="center" gap={8} style={{ marginBottom: 16 }}>
        <BarChartOutlined style={{ color: '#1e3a5f' }} />
        <Typography.Text strong>Manager Statistics</Typography.Text>
      </Flex>
      <Divider style={{ margin: '12px 0' }} />
      <Row gutter={[12, 12]}>
        <Col span={12}>
          <Card size="small" style={{ textAlign: 'center', background: '#f0f9ff' }}>
            <Typography.Title level={4} style={{ margin: 0, color: '#1e3a5f' }}>
              {selectedManagerStats?.totalDealers || 0}
            </Typography.Title>
            <Typography.Text type="secondary">Dealers</Typography.Text>
          </Card>
        </Col>
        <Col span={12}>
          <Card size="small" style={{ textAlign: 'center', background: '#faf5ff' }}>
            <Typography.Title level={4} style={{ margin: 0, color: '#8b5cf6' }}>
              {selectedManagerStats?.totalSubdealers || 0}
            </Typography.Title>
            <Typography.Text type="secondary">Subdealers</Typography.Text>
          </Card>
        </Col>
        <Col span={12}>
          <Flex align="center" gap={8}>
            <BarChartOutlined />
            <div>
              <div style={{ fontWeight: 600 }}>{selectedManagerStats?.totalBids || 0}</div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Total Bids</Typography.Text>
            </div>
          </Flex>
        </Col>
        <Col span={12}>
          <Flex align="center" gap={8}>
            <RiseOutlined style={{ color: '#22c55e' }} />
            <div>
              <div style={{ fontWeight: 600 }}>{selectedManagerStats?.activeBids || 0}</div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Active Bids</Typography.Text>
            </div>
          </Flex>
        </Col>
        <Col span={12}>
          <Flex align="center" gap={8}>
            <TrophyOutlined style={{ color: '#f59e0b' }} />
            <div>
              <div style={{ fontWeight: 600 }}>{selectedManagerStats?.wonAuctions || 0}</div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Won Auctions</Typography.Text>
            </div>
          </Flex>
        </Col>
        <Col span={12}>
          <Flex align="center" gap={8}>
            <RiseOutlined style={{ color: '#3b82f6' }} />
            <div>
              <div style={{ fontWeight: 600 }}>{selectedManagerStats?.successRate?.toFixed(1) || 0}%</div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Success Rate</Typography.Text>
            </div>
          </Flex>
        </Col>
        <Col span={24}>
          <Divider style={{ margin: '12px 0' }} />
          <Flex justify="space-between">
            <Typography.Text type="secondary">Total Revenue</Typography.Text>
            <Typography.Text strong style={{ color: '#22c55e' }}>
              ${(selectedManagerStats?.totalRevenue?.toLocaleString() || 0)}
            </Typography.Text>
          </Flex>
          <Flex justify="space-between" style={{ marginTop: 8 }}>
            <Typography.Text type="secondary">Monthly Revenue</Typography.Text>
            <Typography.Text strong style={{ color: '#1e3a5f' }}>
              ${selectedManagerStats?.monthlyRevenue?.toLocaleString() || 0}
            </Typography.Text>
          </Flex>
        </Col>
      </Row>
    </div>
  );

  const columns = [
    {
      title: 'Manager',
      key: 'manager',
      render: (_, record) => (
        <Flex align="center" gap={8}>
          <Avatar style={{ backgroundColor: '#1e3a5f' }}>
            {record.avatar || record.name?.substring(0, 2).toUpperCase() || 'M'}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600 }}>{record.name}</div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>@{record.user_name}</Typography.Text>
          </div>
        </Flex>
      )
    },
    {
      title: 'Branch',
      key: 'branch',
      render: (_, record) => (
        <Tag icon={<span style={{ fontSize: 14 }}>{getBranchFlag(record.branchId)}</span>} color="blue">
          {getBranchName(record.branchId)}
        </Tag>
      )
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div><PhoneOutlined style={{ marginRight: 4 }} />{record.phone}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}><MailOutlined style={{ marginRight: 4 }} />{record.email}</Typography.Text>
        </div>
      )
    },
    {
      title: 'Stats',
      key: 'stats',
      render: (_, record) => (
        <Popover
          content={statsPopoverContent}
          title={null}
          trigger="click"
          open={statsPopoverOpen === (record.user_id || record.managerId || record.id)}
          onOpenChange={(open) => {
            setStatsPopoverOpen(open ? (record.user_id || record.managerId || record.id) : null);
            if (open) setSelectedManagerStats(record.stats || {});
          }}
        >
          <Space direction="vertical" size={0}>
            <Tag
              icon={<BarChartOutlined />}
              color="blue"
              style={{ cursor: 'pointer' }}
              onClick={(e) => e.stopPropagation()}
            >
              View Stats
            </Tag>
            <Space size={4}>
              <Tag style={{ fontSize: 11 }}>{record.stats?.totalDealers || 0} dealers</Tag>
              <Tag style={{ fontSize: 11 }}>{record.stats?.activeBids || 0} bids</Tag>
            </Space>
          </Space>
        </Popover>
      )
    },
    {
      title: 'Created By',
      key: 'createdBy',
      render: (_, record) => (
        record.createdBy ? (
          <CreatorInfo createdBy={record.createdBy} createdAt={record.createdAt} variant="compact" />
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        )
      )
    },
    ...(isSuperAdmin ? [{
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space onClick={(e) => e.stopPropagation()}>
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} title="Edit Manager" />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              const managerId = record.user_id || record.managerId || record.autoIncrementId || record.id;
              handleDelete(managerId, record.name);
            }}
            title="Delete Manager"
          />
        </Space>
      )
    }] : [])
  ];

  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </Flex>
    );
  }

  return (
    <div style={{ padding: 20 }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 24 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>{t('managers.title')}</Typography.Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadManagers} title={t('common.refresh')} />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            {t('managers.addManager')}
          </Button>
        </Space>
      </Flex>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {stats.map((stat, index) => (
          <Col xs={24} sm={12} md={8} lg={6} key={index}>
            <Card size="small">
              <Flex align="center" gap={8}>
                <span style={{ color: stat.color, fontSize: 20 }}>{stat.icon}</span>
                <div>
                  <Typography.Title level={4} style={{ margin: 0 }}>{stat.value}</Typography.Title>
                  <Typography.Text type="secondary">{stat.title}</Typography.Text>
                </div>
              </Flex>
            </Card>
          </Col>
        ))}
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={managers}
          rowKey={r => r.user_id || r.managerId || r.autoIncrementId || r.id}
          pagination={false}
          onRow={(record) => ({
            onClick: () => handleManagerClick(record),
            style: { cursor: 'pointer' }
          })}
          locale={{ emptyText: 'No managers found' }}
        />
      </Card>

      <Modal
        title={editingManager ? t('managers.editManager') : t('managers.createManager')}
        open={isFormOpen}
        onCancel={() => setIsFormOpen(false)}
        footer={null}
        width={640}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
          initialValues={{
            language: 'en',
            copart_on: true,
            iaai_on: true,
            manheim_on: true,
            prebid: true,
            live_bid: true,
            log_out: false
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
                <Input placeholder="e.g., Giorgi" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
                <Input placeholder="e.g., Beridze" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="username"
                label="Username"
                rules={[{ required: true }, { min: 3 }]}
                extra={editingManager ? 'Username cannot be changed' : 'Minimum 3 characters'}
              >
                <Input placeholder="e.g., manager.ge1" disabled={!!editingManager} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="password"
                label={editingManager ? 'Password (leave empty to keep current)' : 'Password'}
                rules={editingManager ? [] : [{ required: true }, { min: 6 }]}
                extra="Minimum 6 characters"
              >
                <Input.Password placeholder="••••••••" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="confirmPassword"
                label="Confirm Password"
                dependencies={['password']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value && !editingManager) return Promise.reject(new Error('Required'));
                      if (getFieldValue('password') !== value && value) return Promise.reject(new Error('Passwords do not match'));
                      return Promise.resolve();
                    }
                  })
                ]}
              >
                <Input.Password placeholder="••••••••" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="email" label="Email" rules={[{ required: true }, { type: 'email' }]}>
                <Input type="email" placeholder="manager@bidmanager.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
                <Input placeholder="+995 555 111 111" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="branchId" label="Assign to Branch" rules={[{ required: true }]}>
                <Select
                  placeholder="Select branch"
                  options={branches.map(b => ({ value: b.id, label: `${b.flag} ${b.name} - ${b.location}` }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="language" label="Language">
                <Select
                  options={[
                    { value: 'en', label: 'English' },
                    { value: 'ka', label: 'Georgian (ქართული)' },
                    { value: 'hy', label: 'Armenian (Հայերեն)' },
                    { value: 'ru', label: 'Russian (Русский)' },
                    { value: 'ky', label: 'Kyrgyz (Кыргызча)' },
                    { value: 'kk', label: 'Kazakh (Қазақша)' }
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="description" label="Description">
                <Input.TextArea rows={2} placeholder="Brief description about this manager..." />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="note" label="Internal Notes">
                <Input.TextArea rows={3} placeholder="Internal notes (visible to Super Admin only)..." />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>Auction Access</Typography.Text>
              <Space wrap>
                <Form.Item name="copart_on" valuePropName="checked" noStyle>
                  <Checkbox>Copart Access</Checkbox>
                </Form.Item>
                <Form.Item name="iaai_on" valuePropName="checked" noStyle>
                  <Checkbox>IAAI Access</Checkbox>
                </Form.Item>
                <Form.Item name="manheim_on" valuePropName="checked" noStyle>
                  <Checkbox>Manheim Access</Checkbox>
                </Form.Item>
              </Space>
            </Col>
            <Col span={24}>
              <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>Bidding Settings</Typography.Text>
              <Space wrap>
                <Form.Item name="prebid" valuePropName="checked" noStyle>
                  <Checkbox>Prebid Enabled</Checkbox>
                </Form.Item>
                <Form.Item name="live_bid" valuePropName="checked" noStyle>
                  <Checkbox>Live Bid Enabled</Checkbox>
                </Form.Item>
              </Space>
            </Col>
            <Col span={24}>
              <Form.Item name="log_out" valuePropName="checked">
                <Checkbox>Disable Account (User will not be able to login)</Checkbox>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space>
              <Button onClick={() => setIsFormOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                {editingManager ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ManagersPage;
